import React, { useState, useMemo } from 'react';
import {
  Layout,
  Typography,
  Button,
  Table,
  Space,
  Tag,
  Input,
  Tabs,
  Modal,
  Form,
  Select,
  Switch,
  Checkbox,
  message,
  Tooltip,
  Popconfirm,
  Badge,
  Alert,
  Card,
  Spin,
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  DownloadOutlined,
  UploadOutlined,
  EditOutlined,
  DeleteOutlined,
  UserOutlined,
  SafetyOutlined,
  ShopOutlined,
  CodeOutlined,
  LockOutlined,
  SafetyCertificateOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useAuth } from '../contexts/AuthContext';
import { COUNTRIES, getStatesByCountry } from '../data/locationOptions';
import api from '../config/api';
import type { ColumnsType } from 'antd/es/table';
import { Upload } from 'antd';

const { Title, Text } = Typography;
const { Content } = Layout;
const { TabPane } = Tabs;

interface Department {
  id: string;
  name: string;
  description?: string;
  is_active: boolean;
}

interface Role {
  id: string;
  name: string;
  display_name: string;
  description?: string;
  level: number;
  permissions?: Record<string, boolean>;
  is_active: boolean;
  is_system: boolean;
}

interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  phone?: string;
  role: 'admin' | 'agent' | 'sales_agent' | 'sales_rep_internal' | 'developer' | 'resellers' | 'agent_partner' | 'sales_rep_associated';
  company_name?: string;
  country?: string;
  county?: string;
  state?: string;
  parent_agent_partner_id?: string;
  extension?: string;
  ringcentral_extension?: string;
  agent_level?: number;
  is_active: boolean;
  can_edit_businesses?: boolean;
  can_manage_replacements?: boolean;
  departments?: Department[];
}

const UsersAntd: React.FC = () => {
  const { user: currentUser } = useAuth();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [deptForm] = Form.useForm();
  const [roleForm] = Form.useForm();

  // State
  const [activeTab, setActiveTab] = useState('1');
  const [searchTerm, setSearchTerm] = useState('');
  const [createUserModal, setCreateUserModal] = useState(false);
  const [editUserModal, setEditUserModal] = useState(false);
  const [createDeptModal, setCreateDeptModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [confirmBulkOpen, setConfirmBulkOpen] = useState(false);
  const [confirmBulkText, setConfirmBulkText] = useState('');
  
  // Roles state
  const [createRoleModal, setCreateRoleModal] = useState(false);
  const [editRoleModal, setEditRoleModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);

  // User Requests state
  const [approveRequestModal, setApproveRequestModal] = useState(false);
  const [rejectRequestModal, setRejectRequestModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<any>(null);
  const [approveForm] = Form.useForm();

  // State for pagination
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 25,
    total: 0,
  });

  // State for filters
  const [filters, setFilters] = useState({
    role: undefined,
    agent_level: undefined,
    is_active: undefined,
  });

  // Fetch users with pagination and filters
  const { data: usersData, isLoading: loadingUsers } = useQuery(
    ['users', pagination.current, pagination.pageSize, filters, searchTerm],
    () => {
      const params = new URLSearchParams({
        page: pagination.current.toString(),
        limit: pagination.pageSize.toString(),
      });
      
      if (filters.role) params.append('role', filters.role);
      if (filters.agent_level) params.append('agent_level', filters.agent_level);
      if (filters.is_active !== undefined) params.append('is_active', String(filters.is_active));
      if (searchTerm.trim()) params.append('search', searchTerm.trim());
      
      return api.get(`/api/users?${params.toString()}`).then(res => res.data);
    },
    { 
      enabled: currentUser?.role === 'admin',
      keepPreviousData: true
    }
  );

  const users = Array.isArray(usersData?.users) ? usersData.users : [];
  const total = typeof usersData?.total === 'number' ? usersData.total : 0;

  // Fetch departments
  const { data: departmentsData, isLoading: loadingDepts } = useQuery(
    'departments',
    () => api.get('/api/departments').then(res => res.data),
    { enabled: currentUser?.role === 'admin' }
  );

  const departments = Array.isArray(departmentsData?.departments) ? departmentsData.departments : [];

  // Fetch Agent/Partner users for Sales Rep - Associated parent dropdown
  const { data: agentPartnersData } = useQuery(
    ['users', 'agent_partner'],
    () => api.get('/api/users?role=agent_partner&limit=100').then(res => res.data),
    { enabled: (createUserModal || editUserModal) && currentUser?.role === 'admin' }
  );

  // Fetch roles for Permissions tab
  const { data: rolesData, isLoading: loadingRoles } = useQuery(
    'roles',
    () => api.get('/api/roles').then(res => res.data),
    { enabled: currentUser?.role === 'admin' }
  );

  // Fetch permission metadata for Permissions tab
  const { data: permissionsData } = useQuery(
    'permissions',
    () => api.get('/api/permissions').then(res => res.data),
    { enabled: currentUser?.role === 'admin' }
  );

  const roles = Array.isArray(rolesData?.roles) ? rolesData.roles : [];
  const permissionList = Array.isArray(permissionsData?.permissions) ? permissionsData.permissions : [];
  const agentPartnerUsers = (Array.isArray(agentPartnersData?.users) ? agentPartnersData.users : []) as User[];

  // Permissions tab: fetch all users for dropdown (load on Users page so data is ready)
  const { data: allUsersData, isLoading: loadingAllUsers } = useQuery(
    ['users', 'permissions-dropdown'],
    () => api.get('/api/users?limit=100').then(res => res.data),
    { enabled: currentUser?.role === 'admin' }
  );
  const allUsersForPermissions = Array.isArray(allUsersData?.users)
    ? allUsersData.users.filter((u: User) => u.role !== 'admin')
    : [];

  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [selectedRoleId, setSelectedRoleId] = useState<string | null>(null);

  // Fetch user requests
  const { data: requestsData, isLoading: loadingRequests, error: requestsError, refetch: refetchRequests } = useQuery(
    'user-requests',
    async () => {
      try {
        const res = await api.get('/api/users/requests');
        return res.data;
      } catch (error: any) {
        console.error('Error fetching user requests:', error);
        console.error('Error response:', error.response?.data);
        console.error('Error status:', error.response?.status);
        throw error;
      }
    },
    {
      enabled: currentUser?.role === 'admin',
      refetchInterval: 30000, // Refetch every 30 seconds
      retry: false, // Don't retry on error to avoid spam
    }
  );

  // Fetch pending count
  const { data: pendingCountData, error: countError } = useQuery(
    'user-requests-pending-count',
    () => api.get('/api/users/requests/pending/count').then(res => res.data),
    {
      enabled: currentUser?.role === 'admin',
      refetchInterval: 30000,
      onError: (error) => {
        console.error('Error fetching pending count:', error);
      },
    }
  );

  // Extract requests array - handle both { requests: [...] } and direct array
  const requests = Array.isArray(requestsData) ? requestsData : (requestsData?.requests || []);
  const pendingRequests = requests.filter((r: any) => r.status === 'pending');
  const pendingCount = pendingCountData?.count ?? pendingRequests.length;
  
  // Debug logging (remove in production)
  if (currentUser?.role === 'admin') {
    console.log('Requests data:', requestsData);
    console.log('Requests array:', requests);
    console.log('Pending count data:', pendingCountData);
    console.log('Pending count:', pendingCount);
    if (requestsError) console.error('Requests error:', requestsError);
    if (countError) console.error('Count error:', countError);
  }

  // Mutations
  const createUserMutation = useMutation(
    (userData: any) => api.post('/api/users', userData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('users');
        message.success('User created successfully');
        setCreateUserModal(false);
        form.resetFields();
      },
      onError: (error: any) => {
        message.error(error.response?.data?.error || 'Failed to create user');
      }
    }
  );

  const updateUserMutation = useMutation(
    ({ id, data }: { id: string; data: any }) => api.put(`/api/users/${id}`, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('users');
        message.success('User updated successfully');
        setEditUserModal(false);
        setSelectedUser(null);
        form.resetFields();
      },
      onError: (error: any) => {
        message.error(error.response?.data?.error || 'Failed to update user');
      }
    }
  );

  const deleteUserMutation = useMutation(
    (id: string) => api.delete(`/api/users/${id}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('users');
        message.success('User deleted successfully');
      },
      onError: (error: any) => {
        message.error(error.response?.data?.error || 'Failed to delete user');
      }
    }
  );

  // Role mutations
  const createRoleMutation = useMutation(
    (roleData: any) => api.post('/api/roles', roleData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('roles');
        message.success('Role created successfully');
        setCreateRoleModal(false);
        roleForm.resetFields();
      },
      onError: (error: any) => {
        message.error(error.response?.data?.error || 'Failed to create role');
      }
    }
  );

  const updateRoleMutation = useMutation(
    ({ id, data }: { id: string; data: any }) => api.put(`/api/roles/${id}`, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('roles');
        message.success('Role updated successfully');
        setEditRoleModal(false);
        setSelectedRole(null);
        roleForm.resetFields();
      },
      onError: (error: any) => {
        message.error(error.response?.data?.error || 'Failed to update role');
      }
    }
  );

  const deleteRoleMutation = useMutation(
    (id: string) => api.delete(`/api/roles/${id}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('roles');
        message.success('Role deleted successfully');
      },
      onError: (error: any) => {
        message.error(error.response?.data?.error || 'Failed to delete role');
      }
    }
  );

  const [passwordResetConfirm, setPasswordResetConfirm] = useState<{ open: boolean; user: User | null }>({
    open: false,
    user: null,
  });

  const sendPasswordResetMutation = useMutation(
    (userId: string) => api.post('/api/auth/admin/send-password-reset', { user_id: userId }),
    {
      onSuccess: (response: any) => {
        const data = response.data;
        setPasswordResetConfirm({ open: false, user: null });
        if (data.emailSent) {
          message.success(`Password reset link sent to ${data.message.split(' ').pop()}`);
        } else {
          // Show warning with reset link if email failed
          const resetLink = data.resetLink;
          const errorMsg = data.emailError || 'Email could not be sent';
          
          message.warning({
            content: (
              <div>
                <div style={{ marginBottom: 8 }}>
                  <strong>Password reset link generated but email could not be sent.</strong>
                </div>
                <div style={{ marginBottom: 8 }}>
                  <strong>Reason:</strong> {errorMsg}
                </div>
                {resetLink && (
                  <div>
                    <strong>Reset Link:</strong>
                    <div style={{ 
                      marginTop: 4, 
                      padding: '8px', 
                      backgroundColor: '#f5f5f5', 
                      borderRadius: '4px',
                      wordBreak: 'break-all',
                      fontSize: '12px',
                      fontFamily: 'monospace'
                    }}>
                      {resetLink}
                    </div>
                    <div style={{ marginTop: 8 }}>
                      <Button 
                        size="small" 
                        type="link"
                        onClick={() => {
                          navigator.clipboard.writeText(resetLink);
                          message.success('Reset link copied to clipboard!');
                        }}
                      >
                        Copy Link
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ),
            duration: 10, // Show for 10 seconds so admin can copy the link
          });
          
          // Also log to console for easy access
          console.warn('Password reset link (email failed):', resetLink);
        }
      },
      onError: (error: any) => {
        const errorMessage = error.response?.data?.error || 'Failed to send password reset link';
        message.error(errorMessage);
        console.error('Password reset error:', error);
      }
    }
  );

  const handleSendPasswordReset = (user: User) => {
    setPasswordResetConfirm({ open: true, user });
  };

  const confirmSendPasswordReset = () => {
    if (passwordResetConfirm.user) {
      sendPasswordResetMutation.mutate(passwordResetConfirm.user.id);
    }
  };

  // User Requests mutations
  const approveRequestMutation = useMutation(
    ({ requestId, userData }: { requestId: string; userData: any }) =>
      api.post(`/api/users/requests/${requestId}/approve`, userData),
    {
      onSuccess: () => {
        message.success('User request approved and account created successfully');
        setApproveRequestModal(false);
        approveForm.resetFields();
        setSelectedRequest(null);
        queryClient.invalidateQueries('user-requests');
        queryClient.invalidateQueries('user-requests-pending-count');
        queryClient.invalidateQueries('users');
      },
      onError: (error: any) => {
        message.error(error.response?.data?.error || 'Failed to approve user request');
      }
    }
  );

  const rejectRequestMutation = useMutation(
    ({ requestId, reason }: { requestId: string; reason?: string }) =>
      api.post(`/api/users/requests/${requestId}/reject`, { rejection_reason: reason }),
    {
      onSuccess: () => {
        message.success('User request rejected');
        setRejectRequestModal(false);
        setSelectedRequest(null);
        queryClient.invalidateQueries('user-requests');
        queryClient.invalidateQueries('user-requests-pending-count');
      },
      onError: (error: any) => {
        message.error(error.response?.data?.error || 'Failed to reject user request');
      }
    }
  );

  const handleApproveRequest = (request: any) => {
    setSelectedRequest(request);
    approveForm.resetFields();
    // Pre-fill form with request data
    approveForm.setFieldsValue({
      first_name: request.first_name,
      last_name: request.last_name,
      email: request.email,
      phone: request.phone,
    });
    setApproveRequestModal(true);
  };

  const handleRejectRequest = (request: any) => {
    setSelectedRequest(request);
    setRejectRequestModal(true);
  };

  const handleCreateRole = (values: any) => {
    const name = String(values.name || '').trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z_]/g, '_').replace(/_+/g, '_') || 'role';
    createRoleMutation.mutate({
      name,
      display_name: values.display_name.trim(),
      description: values.description?.trim() || undefined,
      level: 0,
      is_active: values.is_active !== false,
      permissions: { access_chat: values.access_chat === true },
    });
  };

  const handleEditRole = (role: Role) => {
    setSelectedRole(role);
    roleForm.setFieldsValue({
      display_name: role.display_name,
      description: role.description || '',
      level: role.level,
      is_active: role.is_active,
      access_chat: role.permissions?.access_chat === true,
    });
    setEditRoleModal(true);
  };

  const handleUpdateRole = (values: any) => {
    if (!selectedRole) return;
    const perms = { ...(selectedRole.permissions || {}), access_chat: values.access_chat === true };
    updateRoleMutation.mutate({
      id: selectedRole.id,
      data: {
        display_name: values.display_name.trim(),
        description: values.description?.trim() || undefined,
        is_active: values.is_active !== false,
        permissions: perms,
      },
    });
  };

  const handleDeleteRole = (role: Role) => {
    deleteRoleMutation.mutate(role.id);
  };

  // Update role permissions mutation
  const updatePermissionsMutation = useMutation(
    ({ roleId, permissions }: { roleId: string; permissions: Record<string, boolean> }) =>
      api.put(`/api/roles/${roleId}/permissions`, { permissions }),
    {
      onSuccess: () => {
        message.success('Role permissions updated');
        queryClient.invalidateQueries('roles');
        queryClient.invalidateQueries('user-permissions');
      },
      onError: (error: any) => {
        message.error(error.response?.data?.error || 'Failed to update permissions');
      }
    }
  );

  const handlePermissionToggle = (roleId: string, permissionKey: string, currentPermissions: Record<string, boolean>, checked: boolean) => {
    const next = { ...(currentPermissions || {}), [permissionKey]: checked };
    updatePermissionsMutation.mutate({ roleId, permissions: next });
  };

  // Fetch selected user's permissions
  const { data: selectedUserPerms, isLoading: loadingUserPerms } = useQuery(
    ['user-permissions', selectedUserId],
    () => api.get(`/api/users/${selectedUserId}/permissions`).then(res => res.data),
    { enabled: !!selectedUserId }
  );

  const updateUserPermissionsMutation = useMutation(
    ({ userId, effective }: { userId: string; effective: Record<string, boolean> }) =>
      api.put(`/api/users/${userId}/permissions`, { effective_permissions: effective }),
    {
      onSuccess: () => {
        message.success('User permissions updated');
        queryClient.invalidateQueries(['user-permissions', selectedUserId]);
        queryClient.invalidateQueries('users');
      },
      onError: (error: any) => {
        message.error(error.response?.data?.error || 'Failed to update permissions');
      }
    }
  );

  const handleUserPermissionChange = (key: string, checked: boolean) => {
    if (!selectedUserId || !selectedUserPerms) return;
    const next = { ...(selectedUserPerms.effective_permissions || {}), [key]: checked };
    updateUserPermissionsMutation.mutate({ userId: selectedUserId, effective: next });
  };

  const confirmApproveRequest = (values: any) => {
    if (selectedRequest) {
      approveRequestMutation.mutate({
        requestId: selectedRequest.id,
        userData: values
      });
    }
  };

  const confirmRejectRequest = (reason?: string) => {
    if (selectedRequest) {
      rejectRequestMutation.mutate({
        requestId: selectedRequest.id,
        reason
      });
    }
  };

  const bulkDeleteUsers = useMutation(
    async (ids: string[]) => {
      await api.post('/api/users/bulk-delete', { ids });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('users');
        message.success('Selected users deleted');
        setSelectedRowKeys([]);
      },
      onError: (error: any) => {
        message.error(error.response?.data?.error || 'Failed to bulk delete users');
      }
    }
  );

  const createDeptMutation = useMutation(
    (deptData: any) => api.post('/api/departments', deptData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('departments');
        message.success('Department created successfully');
        setCreateDeptModal(false);
        deptForm.resetFields();
      },
      onError: (error: any) => {
        message.error(error.response?.data?.error || 'Failed to create department');
      }
    }
  );

  // Table columns for users
  const userColumns: ColumnsType<User> = [
    {
      title: 'Name',
      key: 'name',
      render: (_, record) => (
        <Space>
          {record.role === 'admin' ? (
            <SafetyOutlined style={{ color: '#f44336' }} />
          ) : record.role === 'sales_agent' || record.role === 'sales_rep_internal' ? (
            <ShopOutlined style={{ color: '#ff9800' }} />
          ) : record.role === 'developer' ? (
            <CodeOutlined style={{ color: '#9c27b0' }} />
          ) : (
            <UserOutlined style={{ color: '#1976d2' }} />
          )}
          <span style={{ fontWeight: 600 }}>
            {record.first_name} {record.last_name}
          </span>
        </Space>
      ),
      width: 200,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      width: 220,
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      width: 120,
      render: (role: string) => {
        const colors: Record<string, string> = {
          admin: 'red',
          agent: 'blue',
          sales_agent: 'orange',
          sales_rep_internal: 'orange',
          developer: 'purple',
          resellers: 'cyan',
          agent_partner: 'geekblue',
          sales_rep_associated: 'lime',
        };
        const labels: Record<string, string> = {
          sales_rep_internal: 'Sales Rep - Internal',
          sales_rep_associated: 'Sales Rep - Associated',
          agent_partner: 'Agent/Partner',
          resellers: 'Resellers',
        };
        const label = labels[role] || role.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
        return (
          <Tag color={colors[role] || 'default'}>
            {label}
          </Tag>
        );
      },
    },
    {
      title: 'Agent Level',
      dataIndex: 'agent_level',
      key: 'agent_level',
      width: 100,
      align: 'center',
      render: (level, record: User) => record.role === 'agent' ? (level || '-') : '—',
    },
    {
      title: 'Departments',
      key: 'departments',
      width: 150,
      align: 'center',
      render: (_, record) => {
        const depts = record.departments || [];
        if (depts.length === 0) return '-';
        
        const deptNames = depts.map(d => d.name).join(', ');
        return (
          <Tooltip title={deptNames}>
            <Badge count={depts.length} showZero={false} style={{ backgroundColor: '#1976d2' }} />
          </Tooltip>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 100,
      render: (active: boolean) => (
        <Tag color={active ? 'success' : 'default'}>
          {active ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Edit">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
              size="small"
            />
          </Tooltip>
          <Tooltip title={currentUser?.role === 'admin' ? 'Send Password Reset Link' : 'Admin only'}>
            <Button
              type="text"
              icon={<LockOutlined />}
              onClick={() => handleSendPasswordReset(record)}
              disabled={currentUser?.role !== 'admin' || !record.email}
              size="small"
            />
          </Tooltip>
          <Tooltip title={currentUser?.role === 'admin' ? 'Delete' : 'Admin only'}>
            <Popconfirm
              title="Delete user"
              description="Are you sure you want to delete this user?"
              onConfirm={() => deleteUserMutation.mutate(record.id)}
              disabled={currentUser?.role !== 'admin'}
            >
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                disabled={currentUser?.role !== 'admin'}
                size="small"
              />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  // Department columns
  const deptColumns: ColumnsType<Department> = [
    {
      title: 'Department Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (desc) => desc || '-',
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (active: boolean) => (
        <Tag color={active ? 'success' : 'default'}>
          {active ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
  ];

  // Since search is now server-side, we don't need client-side filtering
  // But we keep this for any additional client-side filtering if needed
  const filteredUsers = users;

  const handleEdit = (user: User) => {
    setSelectedUser(user);
    form.setFieldsValue({
      first_name: user.first_name,
      last_name: user.last_name,
      email: user.email,
      phone: user.phone,
      role: user.role,
      agent_level: user.agent_level,
      department_ids: (user.departments || []).map(d => d.id),
      ringcentral_extension: user.ringcentral_extension,
      is_active: user.is_active,
      company_name: user.company_name,
      country: user.country,
      county: user.county,
      state: user.state,
      parent_agent_partner_id: user.parent_agent_partner_id,
    });
    setEditUserModal(true);
  };

  const ROLES_NO_DEPT_OR_RINGCENTRAL = ['resellers', 'agent_partner', 'sales_rep_internal'];

  const handleCreateUser = (values: any) => {
    const payload = { ...values };
    if (ROLES_NO_DEPT_OR_RINGCENTRAL.includes(values.role)) {
      payload.department_ids = [];
      payload.ringcentral_extension = undefined;
    }
    if (values.role !== 'agent') payload.agent_level = null;
    createUserMutation.mutate(payload);
  };

  const handleUpdateUser = (values: any) => {
    if (selectedUser) {
      const { password, ...data } = values;
      let updateData = password ? values : data;
      if (ROLES_NO_DEPT_OR_RINGCENTRAL.includes(updateData.role)) {
        updateData = { ...updateData, department_ids: [], ringcentral_extension: undefined };
      }
      if (updateData.role !== 'agent') updateData.agent_level = null;
      updateUserMutation.mutate({ id: selectedUser.id, data: updateData });
    }
  };

  const handleCreateDept = (values: any) => {
    createDeptMutation.mutate(values);
  };

  if (currentUser?.role !== 'admin') {
    return (
      <Layout style={{ minHeight: '100vh', background: '#fff' }}>
        <Content style={{ padding: 24 }}>
          <Title level={4}>Access Denied</Title>
          <p>You don't have permission to view this page.</p>
        </Content>
      </Layout>
    );
  }

  return (
    <Layout style={{ minHeight: '100vh', background: '#fff' }}>
      <Content style={{ padding: 24 }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <Title level={2} style={{ margin: 0 }}>Users</Title>
          <Space>
            {activeTab === '1' && (
              <>
                <Button icon={<DownloadOutlined />}>Export</Button>
                <Button danger disabled={selectedRowKeys.length === 0} onClick={() => { setConfirmBulkText(''); setConfirmBulkOpen(true); }}>Bulk Delete</Button>
                <Button icon={<UploadOutlined />} onClick={() => setBulkUploadOpen(true)}>Bulk Upload</Button>
                <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateUserModal(true)}>
                  Add User
                </Button>
              </>
            )}
            {activeTab === '2' && (
              <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateDeptModal(true)}>
                Add Department
              </Button>
            )}
            {activeTab === '5' && (
              <Button type="primary" icon={<PlusOutlined />} onClick={() => { roleForm.resetFields(); setCreateRoleModal(true); }}>
                Add Role
              </Button>
            )}
          </Space>
        </div>

        {/* Tabs */}
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane tab={`User Management (${users.length})`} key="1">
            {/* Search */}
            <Input
              placeholder="Search users by name, email, phone..."
              prefix={<SearchOutlined />}
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPagination(prev => ({ ...prev, current: 1 }));
              }}
              style={{ marginBottom: 16, maxWidth: 400 }}
              allowClear
            />

            {/* Filter Controls */}
            <div style={{ marginBottom: 16, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              <Select
                placeholder="Filter by Role"
                value={filters.role}
                onChange={(value) => setFilters(prev => ({ ...prev, role: value }))}
                style={{ width: 180 }}
                allowClear
                loading={loadingRoles}
                showSearch
                optionFilterProp="label"
                options={roles.map((r: Role) => ({ value: r.name, label: r.display_name || r.name }))}
              />

              <Select
                placeholder="Agent Level (1-3)"
                value={filters.agent_level}
                onChange={(value) => setFilters(prev => ({ ...prev, agent_level: value }))}
                style={{ width: 150 }}
                allowClear
              >
                <Select.Option value="1">Level 1</Select.Option>
                <Select.Option value="2">Level 2</Select.Option>
                <Select.Option value="3">Level 3</Select.Option>
              </Select>

              <Select
                placeholder="Filter by Status"
                value={filters.is_active}
                onChange={(value) => setFilters(prev => ({ ...prev, is_active: value }))}
                style={{ width: 150 }}
                allowClear
              >
                <Select.Option value={true}>Active</Select.Option>
                <Select.Option value={false}>Inactive</Select.Option>
              </Select>

              <Button 
                onClick={() => {
                  setFilters({ role: undefined, agent_level: undefined, is_active: undefined });
                  setPagination(prev => ({ ...prev, current: 1 }));
                }}
                type="default"
              >
                Clear Filters
              </Button>
            </div>

              {/* Users Table */}
            <Table
                    columns={userColumns}
              dataSource={filteredUsers}
              rowKey="id"
              loading={loadingUsers}
              rowSelection={{
                selectedRowKeys,
                onChange: (keys) => setSelectedRowKeys(keys),
                preserveSelectedRowKeys: true,
              }}
              pagination={{
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: total,
                showSizeChanger: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} users`,
                onChange: (page, pageSize) => {
                  setPagination(prev => ({
                    ...prev,
                    current: page,
                    pageSize: pageSize || prev.pageSize,
                  }));
                },
                onShowSizeChange: (current, size) => {
                  setPagination(prev => ({
                    ...prev,
                    current: 1,
                    pageSize: size,
                  }));
                },
              }}
              scroll={{ x: 1200 }}
            />
          </TabPane>

          <TabPane 
            tab={
              <span>
                User Requests
                {pendingCount > 0 && (
                  <Badge count={pendingCount} offset={[8, 0]} />
                )}
              </span>
            } 
            key="3"
          >
            <Table
              columns={[
                {
                  title: 'Name',
                  key: 'name',
                  render: (_, record: any) => `${record.first_name} ${record.last_name}`,
                },
                {
                  title: 'Email',
                  dataIndex: 'email',
                  key: 'email',
                },
                {
                  title: 'Phone',
                  dataIndex: 'phone',
                  key: 'phone',
                  render: (phone) => phone || '-',
                },
                {
                  title: 'Notes',
                  dataIndex: 'notes',
                  key: 'notes',
                  render: (notes) => notes || '-',
                  ellipsis: true,
                },
                {
                  title: 'Status',
                  dataIndex: 'status',
                  key: 'status',
                  render: (status: string) => (
                    <Tag color={status === 'pending' ? 'orange' : status === 'approved' ? 'green' : 'red'}>
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </Tag>
                  ),
                },
                {
                  title: 'Requested',
                  dataIndex: 'created_at',
                  key: 'created_at',
                  render: (date: string) => new Date(date).toLocaleDateString(),
                },
                {
                  title: 'Actions',
                  key: 'actions',
                  render: (_: any, record: any) => (
                    <Space>
                      {record.status === 'pending' && (
                        <>
                          <Button
                            type="primary"
                            size="small"
                            onClick={() => handleApproveRequest(record)}
                          >
                            Approve
                          </Button>
                          <Button
                            danger
                            size="small"
                            onClick={() => handleRejectRequest(record)}
                          >
                            Reject
                          </Button>
                        </>
                      )}
                      {record.status !== 'pending' && record.reviewer_first_name && (
                        <span style={{ fontSize: '12px', color: '#8c8c8c' }}>
                          Reviewed by {record.reviewer_first_name} {record.reviewer_last_name}
                        </span>
                      )}
                    </Space>
                  ),
                },
              ]}
              dataSource={requests}
              rowKey="id"
              loading={loadingRequests}
              locale={{
                emptyText: requestsError ? (
                  <div>
                    <div style={{ color: '#ff4d4f', marginBottom: 8 }}>Error loading requests</div>
                    <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
                      {(requestsError as any)?.response?.data?.error || (requestsError as any)?.message || 'Unknown error'}
                    </div>
                    <Button 
                      size="small" 
                      type="link" 
                      onClick={() => refetchRequests()}
                      style={{ marginTop: 8 }}
                    >
                      Retry
                    </Button>
                  </div>
                ) : 'No requests found'
              }}
              pagination={{
                pageSize: 10,
                showTotal: (total) => `Total ${total} requests`,
              }}
            />
          </TabPane>

          <TabPane tab={`Departments (${departments.length})`} key="2">
            <Table
              columns={deptColumns}
              dataSource={departments}
              rowKey="id"
              loading={loadingDepts}
              pagination={{
                pageSize: 10,
                showTotal: (total) => `Total ${total} departments`,
              }}
            />
          </TabPane>

          <TabPane
            tab={
              <span>
                <SafetyCertificateOutlined style={{ marginRight: 4 }} />
                Permissions
              </span>
            }
            key="4"
          >
            <Card>
              {/* By Role */}
              <div style={{ marginBottom: 32 }}>
                <Text strong style={{ display: 'block', marginBottom: 8 }}>By Role</Text>
                <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                  Assign permissions to a role. Everyone with that role gets these permissions.
                </Text>
                <Select
                  placeholder="Select role..."
                  allowClear
                  loading={loadingRoles}
                  style={{ width: '100%', maxWidth: 400, marginBottom: 16 }}
                  value={selectedRoleId || undefined}
                  onChange={(v) => setSelectedRoleId(v || null)}
                  options={roles.filter((r: Role) => r.name !== 'admin').map((r: Role) => ({
                    value: r.id,
                    label: `${r.display_name} (${r.name})`,
                  }))}
                  notFoundContent={loadingRoles ? 'Loading...' : 'No roles found'}
                />
                {selectedRoleId && (() => {
                  const selRole = roles.find((r: Role) => r.id === selectedRoleId);
                  if (!selRole) return null;
                  return (
                    <div style={{ padding: '12px 0', borderTop: '1px solid #f0f0f0' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 12 }}>
                        {permissionList.map((p: { key: string; label: string }) => (
                          <div key={p.key} style={{ padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                            <Checkbox
                              checked={selRole.permissions?.[p.key] === true}
                              onChange={(e) => handlePermissionToggle(selRole.id, p.key, selRole.permissions || {}, e.target.checked)}
                              disabled={updatePermissionsMutation.isLoading}
                            >
                              {p.label}
                            </Checkbox>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* By User */}
              <div>
                <Text strong style={{ display: 'block', marginBottom: 8 }}>By User</Text>
                <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                  Override permissions for a specific user. User overrides take precedence over role permissions.
                </Text>
                <Select
                  placeholder="Search by name or email..."
                  showSearch
                  allowClear
                  loading={loadingAllUsers}
                  style={{ width: '100%', maxWidth: 480 }}
                  value={selectedUserId || undefined}
                  onChange={(v) => setSelectedUserId(v || null)}
                  optionFilterProp="label"
                  filterOption={(input, opt) =>
                    String(opt?.label ?? '').toLowerCase().includes((input || '').toLowerCase())
                  }
                  options={allUsersForPermissions.map((u: User) => ({
                    value: u.id,
                    label: `${u.first_name} ${u.last_name} (${u.email}) — ${u.role}`
                  }))}
                  notFoundContent={loadingAllUsers ? 'Loading users...' : 'No users found'}
                />
              </div>

              {selectedUserId && (
                loadingUserPerms ? (
                  <div style={{ padding: 48, textAlign: 'center' }}>
                    <Spin size="large" />
                  </div>
                ) : selectedUserPerms ? (
                  <div style={{ marginTop: 20, paddingTop: 20, borderTop: '1px solid #f0f0f0' }}>
                    <Text strong style={{ display: 'block', marginBottom: 4 }}>
                      {selectedUserPerms.user_name} <Tag>{selectedUserPerms.role}</Tag>
                    </Text>
                    <Text type="secondary" style={{ display: 'block', marginBottom: 20 }}>
                      User-level overrides. Admins always have full access.
                    </Text>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 12 }}>
                      {permissionList.map((p: { key: string; label: string }) => (
                        <div key={p.key} style={{ padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                          <Checkbox
                            checked={selectedUserPerms.effective_permissions?.[p.key] === true}
                            onChange={(e) => handleUserPermissionChange(p.key, e.target.checked)}
                            disabled={updateUserPermissionsMutation.isLoading}
                          >
                            {p.label}
                          </Checkbox>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null
              )}

              {!selectedUserId && !selectedRoleId && (
                <Text type="secondary" style={{ display: 'block', marginTop: 16 }}>Select a role or user above to assign permissions.</Text>
              )}
            </Card>
          </TabPane>

          <TabPane
            tab={
              <span>
                <TeamOutlined style={{ marginRight: 4 }} />
                Roles ({roles.length})
              </span>
            }
            key="5"
          >
            <Table
              columns={[
                { title: 'Display Name', dataIndex: 'display_name', key: 'display_name', render: (v: string, r: Role) => <Text strong>{v}</Text> },
                { title: 'Name', dataIndex: 'name', key: 'name', render: (v: string) => <Text type="secondary" style={{ fontFamily: 'monospace' }}>{v}</Text> },
                {
                  title: 'Access to Chat',
                  key: 'access_chat',
                  width: 120,
                  render: (_: any, r: Role) => r.permissions?.access_chat === true ? <Tag color="green">Yes</Tag> : <Tag>No</Tag>,
                },
                {
                  title: 'Active',
                  dataIndex: 'is_active',
                  key: 'is_active',
                  width: 80,
                  render: (v: boolean) => v ? <Tag color="green">Active</Tag> : <Tag>Inactive</Tag>,
                },
                {
                  title: 'Actions',
                  key: 'actions',
                  width: 120,
                  render: (_: any, r: Role) => (
                    <Space>
                      <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEditRole(r)}>
                        Edit
                      </Button>
                      {!r.is_system && (
                        <Popconfirm
                          title="Delete this role?"
                          description="Users must be reassigned before deleting."
                          onConfirm={() => handleDeleteRole(r)}
                          okText="Delete"
                          okButtonProps={{ danger: true }}
                        >
                          <Button type="link" size="small" danger icon={<DeleteOutlined />}>Delete</Button>
                        </Popconfirm>
                      )}
                    </Space>
                  ),
                },
              ]}
              dataSource={roles}
              rowKey="id"
              loading={loadingRoles}
              pagination={{ pageSize: 20, showTotal: (t) => `Total ${t} roles` }}
            />
          </TabPane>
        </Tabs>

        {/* Create User Modal */}
        <Modal
          title="Create New User"
          open={createUserModal}
          onCancel={() => {
            setCreateUserModal(false);
            form.resetFields();
          }}
          onOk={() => form.submit()}
          width={600}
          okText="Create User"
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleCreateUser}
            style={{ marginTop: 16 }}
          >
            <Form.Item
              name="first_name"
                label="First Name"
              rules={[{ required: true, message: 'Please enter first name' }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="last_name"
                label="Last Name"
              rules={[{ required: true, message: 'Please enter last name' }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="email"
              label="Email"
              rules={[
                { required: true, message: 'Please enter email' },
                { type: 'email', message: 'Please enter a valid email' }
              ]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="password"
              label="Password"
              rules={[{ required: true, message: 'Please enter password' }]}
            >
              <Input.Password />
            </Form.Item>

            <Form.Item name="phone" label="Phone">
              <Input />
            </Form.Item>

            <Form.Item
              name="role"
              label="Role"
              rules={[{ required: true, message: 'Please select role' }]}
              initialValue="agent"
            >
              <Select
                placeholder="Select role"
                loading={loadingRoles}
                showSearch
                optionFilterProp="label"
                options={roles.map((r: Role) => ({ value: r.name, label: r.display_name || r.name }))}
              />
            </Form.Item>

            <Form.Item noStyle shouldUpdate={(prev, curr) => prev.role !== curr.role}>
              {({ getFieldValue }) =>
                ['resellers', 'agent_partner'].includes(getFieldValue('role')) ? (
                  <>
                    <Form.Item name="company_name" label="Company Name">
                      <Input />
                    </Form.Item>
                    <Form.Item name="country" label="Country">
                      <Select
                        placeholder="Select country"
                        allowClear
                        options={COUNTRIES.map((c) => ({ value: c.code, label: c.name }))}
                        onChange={() => form.setFieldValue('state', undefined)}
                      />
                    </Form.Item>
                    <Form.Item noStyle shouldUpdate={(prev, curr) => prev.country !== curr.country || prev.role !== curr.role}>
                      {({ getFieldValue }) => {
                        const country = getFieldValue('country');
                        const states = getStatesByCountry(country);
                        return (
                          <Form.Item name="state" label="State">
                            <Select
                              placeholder={country ? 'Select state' : 'Select country first'}
                              allowClear
                              disabled={!country}
                              options={states.map((s) => ({ value: s.code, label: s.name }))}
                            />
                          </Form.Item>
                        );
                      }}
                    </Form.Item>
                    <Form.Item name="county" label="County">
                      <Input placeholder="County (optional)" />
                    </Form.Item>
                  </>
                ) : null
              }
            </Form.Item>
            <Form.Item noStyle shouldUpdate={(prev, curr) => prev.role !== curr.role}>
              {({ getFieldValue }) =>
                getFieldValue('role') === 'sales_rep_associated' ? (
                  <>
                    <Form.Item
                      name="parent_agent_partner_id"
                      label="Agent/Partner (Parent)"
                      rules={[{ required: true, message: 'Select the parent Agent/Partner' }]}
                    >
                      <Select
                        placeholder="Select Agent/Partner"
                        showSearch
                        optionFilterProp="label"
                        options={agentPartnerUsers.map((u) => ({
                          value: u.id,
                          label: `${u.first_name} ${u.last_name}${u.company_name ? ` (${u.company_name})` : ''}`,
                        }))}
                        onChange={(value) => {
                          const parent = agentPartnerUsers.find((u) => u.id === value);
                          if (parent?.company_name) form.setFieldValue('company_name', parent.company_name);
                        }}
                      />
                    </Form.Item>
                    <Form.Item name="company_name" label="Company (from parent)">
                      <Input disabled placeholder="Auto from Agent/Partner" />
                    </Form.Item>
                    <Form.Item name="country" label="Country">
                      <Select
                        placeholder="Select country"
                        allowClear
                        options={COUNTRIES.map((c) => ({ value: c.code, label: c.name }))}
                        onChange={() => form.setFieldValue('state', undefined)}
                      />
                    </Form.Item>
                    <Form.Item noStyle shouldUpdate={(prev, curr) => prev.country !== curr.country || prev.role !== curr.role}>
                      {({ getFieldValue }) => {
                        const country = getFieldValue('country');
                        const states = getStatesByCountry(country);
                        return (
                          <Form.Item name="state" label="State">
                            <Select
                              placeholder={country ? 'Select state' : 'Select country first'}
                              allowClear
                              disabled={!country}
                              options={states.map((s) => ({ value: s.code, label: s.name }))}
                            />
                          </Form.Item>
                        );
                      }}
                    </Form.Item>
                    <Form.Item name="county" label="County">
                      <Input placeholder="County (optional)" />
                    </Form.Item>
                  </>
                ) : null
              }
            </Form.Item>

            <Form.Item noStyle shouldUpdate={(prev, curr) => prev.role !== curr.role}>
              {({ getFieldValue }) => {
                const role = getFieldValue('role');
                const showDeptAndRingCentral = role && !['resellers', 'agent_partner', 'sales_rep_internal'].includes(role);
                return showDeptAndRingCentral ? (
                  <Form.Item name="department_ids" label="Departments">
                    <Select mode="multiple" placeholder="Select departments">
                      {departments.map((dept: Department) => (
                        <Select.Option key={dept.id} value={dept.id}>
                          {dept.name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                ) : null;
              }}
            </Form.Item>

            <Form.Item noStyle shouldUpdate={(prev, curr) => prev.role !== curr.role}>
              {({ getFieldValue }) =>
                getFieldValue('role') === 'agent' ? (
                  <Form.Item
                    name="agent_level"
                    label="Agent Level"
                    initialValue={1}
                    tooltip="Seniority for ticket escalation. Level 1 (basic) to Level 3 (expert). Only agents have levels."
                  >
                    <Select>
                      <Select.Option value={1}>Level 1</Select.Option>
                      <Select.Option value={2}>Level 2</Select.Option>
                      <Select.Option value={3}>Level 3</Select.Option>
                    </Select>
                  </Form.Item>
                ) : null
              }
            </Form.Item>

            <Form.Item noStyle shouldUpdate={(prev, curr) => prev.role !== curr.role}>
              {({ getFieldValue }) => {
                const role = getFieldValue('role');
                const showRingCentral = role && !['resellers', 'agent_partner', 'sales_rep_internal'].includes(role);
                return showRingCentral ? (
                  <Form.Item name="ringcentral_extension" label="RingCentral Extension">
                    <Input />
                  </Form.Item>
                ) : null;
              }}
            </Form.Item>

            <Form.Item name="is_active" label="Active" valuePropName="checked" initialValue={true}>
              <Switch />
            </Form.Item>
          </Form>
        </Modal>

        {/* Edit User Modal */}
        <Modal
          title="Edit User"
          open={editUserModal}
          onCancel={() => {
            setEditUserModal(false);
            setSelectedUser(null);
            form.resetFields();
          }}
          onOk={() => form.submit()}
          width={600}
          okText="Update User"
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleUpdateUser}
            style={{ marginTop: 16 }}
          >
            <Form.Item
              name="first_name"
                label="First Name"
              rules={[{ required: true, message: 'Please enter first name' }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="last_name"
                label="Last Name"
              rules={[{ required: true, message: 'Please enter last name' }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="email"
              label="Email"
              rules={[
                { required: true, message: 'Please enter email' },
                { type: 'email', message: 'Please enter a valid email' }
              ]}
            >
              <Input />
            </Form.Item>

            <Form.Item name="phone" label="Phone">
              <Input />
            </Form.Item>

            <Form.Item name="password" label="New Password (leave blank to keep current)">
              <Input.Password />
            </Form.Item>

            <Form.Item
              name="role"
              label="Role"
              rules={[{ required: true, message: 'Please select role' }]}
            >
              <Select
                placeholder="Select role"
                loading={loadingRoles}
                showSearch
                optionFilterProp="label"
                options={roles.map((r: Role) => ({ value: r.name, label: r.display_name || r.name }))}
              />
            </Form.Item>

            <Form.Item noStyle shouldUpdate={(prev, curr) => prev.role !== curr.role}>
              {({ getFieldValue }) =>
                ['resellers', 'agent_partner'].includes(getFieldValue('role')) ? (
                  <>
                    <Form.Item name="company_name" label="Company Name">
                      <Input />
                    </Form.Item>
                    <Form.Item name="country" label="Country">
                      <Select
                        placeholder="Select country"
                        allowClear
                        options={COUNTRIES.map((c) => ({ value: c.code, label: c.name }))}
                        onChange={() => form.setFieldValue('state', undefined)}
                      />
                    </Form.Item>
                    <Form.Item noStyle shouldUpdate={(prev, curr) => prev.country !== curr.country || prev.role !== curr.role}>
                      {({ getFieldValue }) => {
                        const country = getFieldValue('country');
                        const states = getStatesByCountry(country);
                        return (
                          <Form.Item name="state" label="State">
                            <Select
                              placeholder={country ? 'Select state' : 'Select country first'}
                              allowClear
                              disabled={!country}
                              options={states.map((s) => ({ value: s.code, label: s.name }))}
                            />
                          </Form.Item>
                        );
                      }}
                    </Form.Item>
                    <Form.Item name="county" label="County">
                      <Input placeholder="County (optional)" />
                    </Form.Item>
                  </>
                ) : null
              }
            </Form.Item>
            <Form.Item noStyle shouldUpdate={(prev, curr) => prev.role !== curr.role}>
              {({ getFieldValue }) =>
                getFieldValue('role') === 'sales_rep_associated' ? (
                  <>
                    <Form.Item
                      name="parent_agent_partner_id"
                      label="Agent/Partner (Parent)"
                      rules={[{ required: true, message: 'Select the parent Agent/Partner' }]}
                    >
                      <Select
                        placeholder="Select Agent/Partner"
                        showSearch
                        optionFilterProp="label"
                        options={agentPartnerUsers.map((u) => ({
                          value: u.id,
                          label: `${u.first_name} ${u.last_name}${u.company_name ? ` (${u.company_name})` : ''}`,
                        }))}
                        onChange={(value) => {
                          const parent = agentPartnerUsers.find((u) => u.id === value);
                          if (parent?.company_name) form.setFieldValue('company_name', parent.company_name);
                        }}
                      />
                    </Form.Item>
                    <Form.Item name="company_name" label="Company (from parent)">
                      <Input disabled placeholder="Auto from Agent/Partner" />
                    </Form.Item>
                    <Form.Item name="country" label="Country">
                      <Select
                        placeholder="Select country"
                        allowClear
                        options={COUNTRIES.map((c) => ({ value: c.code, label: c.name }))}
                        onChange={() => form.setFieldValue('state', undefined)}
                      />
                    </Form.Item>
                    <Form.Item noStyle shouldUpdate={(prev, curr) => prev.country !== curr.country || prev.role !== curr.role}>
                      {({ getFieldValue }) => {
                        const country = getFieldValue('country');
                        const states = getStatesByCountry(country);
                        return (
                          <Form.Item name="state" label="State">
                            <Select
                              placeholder={country ? 'Select state' : 'Select country first'}
                              allowClear
                              disabled={!country}
                              options={states.map((s) => ({ value: s.code, label: s.name }))}
                            />
                          </Form.Item>
                        );
                      }}
                    </Form.Item>
                    <Form.Item name="county" label="County">
                      <Input placeholder="County (optional)" />
                    </Form.Item>
                  </>
                ) : null
              }
            </Form.Item>

            <Form.Item noStyle shouldUpdate={(prev, curr) => prev.role !== curr.role}>
              {({ getFieldValue }) => {
                const role = getFieldValue('role');
                const showDeptAndRingCentral = role && !['resellers', 'agent_partner', 'sales_rep_internal'].includes(role);
                return showDeptAndRingCentral ? (
                  <Form.Item name="department_ids" label="Departments">
                    <Select mode="multiple" placeholder="Select departments">
                      {departments.map((dept: Department) => (
                        <Select.Option key={dept.id} value={dept.id}>
                          {dept.name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                ) : null;
              }}
            </Form.Item>

            <Form.Item noStyle shouldUpdate={(prev, curr) => prev.role !== curr.role}>
              {({ getFieldValue }) =>
                getFieldValue('role') === 'agent' ? (
                  <Form.Item
                    name="agent_level"
                    label="Agent Level"
                    tooltip="Seniority for ticket escalation. Level 1 (basic) to Level 3 (expert). Only agents have levels."
                  >
                    <Select>
                      <Select.Option value={1}>Level 1</Select.Option>
                      <Select.Option value={2}>Level 2</Select.Option>
                      <Select.Option value={3}>Level 3</Select.Option>
                    </Select>
                  </Form.Item>
                ) : null
              }
            </Form.Item>

            <Form.Item noStyle shouldUpdate={(prev, curr) => prev.role !== curr.role}>
              {({ getFieldValue }) => {
                const role = getFieldValue('role');
                const showRingCentral = role && !['resellers', 'agent_partner', 'sales_rep_internal'].includes(role);
                return showRingCentral ? (
                  <Form.Item name="ringcentral_extension" label="RingCentral Extension">
                    <Input />
                  </Form.Item>
                ) : null;
              }}
            </Form.Item>

            <Form.Item name="is_active" label="Active" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Form>
        </Modal>

        {/* Create Department Modal */}
        <Modal
          title="Create Department"
          open={createDeptModal}
          onCancel={() => {
            setCreateDeptModal(false);
            deptForm.resetFields();
          }}
          onOk={() => deptForm.submit()}
          okText="Create Department"
        >
          <Form
            form={deptForm}
            layout="vertical"
            onFinish={handleCreateDept}
            style={{ marginTop: 16 }}
          >
            <Form.Item
              name="name"
              label="Department Name"
              rules={[{ required: true, message: 'Please enter department name' }]}
            >
              <Input />
            </Form.Item>

            <Form.Item name="description" label="Description">
              <Input.TextArea rows={3} />
            </Form.Item>
          </Form>
        </Modal>

        {/* Create Role Modal */}
        <Modal
          title="Create Role"
          open={createRoleModal}
          onCancel={() => {
            setCreateRoleModal(false);
            roleForm.resetFields();
          }}
          onOk={() => roleForm.submit()}
          okText="Create Role"
          confirmLoading={createRoleMutation.isLoading}
        >
          <Form
            form={roleForm}
            layout="vertical"
            onFinish={handleCreateRole}
            style={{ marginTop: 16 }}
          >
            <Form.Item
              name="name"
              label="Role Name (internal)"
              rules={[{ required: true, message: 'Required' }]}
            >
              <Input placeholder="e.g. support_lead" />
            </Form.Item>

            <Form.Item name="display_name" label="Display Name" rules={[{ required: true, message: 'Required' }]}>
              <Input placeholder="e.g. Support Lead" />
            </Form.Item>

            <Form.Item name="description" label="Description">
              <Input.TextArea rows={2} placeholder="Optional" />
            </Form.Item>

            <Form.Item name="access_chat" label="Access to Chat" valuePropName="checked" initialValue={false}>
              <Checkbox>Users with this role can access Chat</Checkbox>
            </Form.Item>

            <Form.Item name="is_active" label="Active" valuePropName="checked" initialValue={true}>
              <Switch />
            </Form.Item>
          </Form>
        </Modal>

        {/* Edit Role Modal */}
        <Modal
          title="Edit Role"
          open={editRoleModal}
          onCancel={() => {
            setEditRoleModal(false);
            setSelectedRole(null);
            roleForm.resetFields();
          }}
          onOk={() => roleForm.submit()}
          okText="Save"
          confirmLoading={updateRoleMutation.isLoading}
        >
          {selectedRole && (
            <Form
              form={roleForm}
              layout="vertical"
              onFinish={handleUpdateRole}
              style={{ marginTop: 16 }}
            >
              <Form.Item label="Role Name (internal)">
                <Input value={selectedRole.name} disabled style={{ fontFamily: 'monospace' }} />
              </Form.Item>

              <Form.Item name="display_name" label="Display Name" rules={[{ required: true, message: 'Required' }]}>
                <Input />
              </Form.Item>

              <Form.Item name="description" label="Description">
                <Input.TextArea rows={2} />
              </Form.Item>

              <Form.Item name="access_chat" label="Access to Chat" valuePropName="checked">
                <Checkbox>Users with this role can access Chat</Checkbox>
              </Form.Item>

              <Form.Item name="is_active" label="Active" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Form>
          )}
        </Modal>
      </Content>
      {/* Bulk Upload Modal (match Businesses style) */}
      <Modal
        title="Bulk Upload Users"
        open={bulkUploadOpen}
        onCancel={() => setBulkUploadOpen(false)}
        footer={[
          <Button key="cancel" onClick={() => setBulkUploadOpen(false)}>
            Cancel
          </Button>,
          <Button
            key="download"
            type="link"
            href="/templates/user_template.csv"
            download
          >
            Download Template
          </Button>,
        ]}
      >
        <p>Upload a CSV file with user data. Make sure to follow the template format.</p>
        <Upload.Dragger
          accept=".csv"
          beforeUpload={async (file) => {
            const formData = new FormData();
            formData.append('file', file as any);
            try {
              const res = await api.post('/api/users/bulk-upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
              });
              const data = res.data || {};
              if (data.success) message.success(`Uploaded ${data.success} users`);
              if (Array.isArray(data.errors) && data.errors.length) message.error(`${data.errors.length} rows had errors`);
              queryClient.invalidateQueries('users');
              setBulkUploadOpen(false);
            } catch (e: any) {
              message.error(e?.response?.data?.error || 'Failed to upload');
            }
            return false;
          }}
          maxCount={1}
        >
          <p className="ant-upload-drag-icon">
            <UploadOutlined />
          </p>
          <p className="ant-upload-text">Click or drag CSV file to upload</p>
          <p className="ant-upload-hint">Only CSV files are supported</p>
        </Upload.Dragger>
      </Modal>

      {/* Confirm Bulk Delete */}
      <Modal
        title={`Confirm Bulk Delete (${selectedRowKeys.length} user(s))`}
        open={confirmBulkOpen}
        onCancel={() => setConfirmBulkOpen(false)}
        onOk={() => { bulkDeleteUsers.mutate(selectedRowKeys as string[]); setConfirmBulkOpen(false); }}
        okButtonProps={{ danger: true, disabled: confirmBulkText !== 'DELETE', children: 'Delete' }}
      >
        <Typography.Paragraph type="secondary">
          This action cannot be undone. Type DELETE to confirm.
        </Typography.Paragraph>
        <Input placeholder="Type DELETE" value={confirmBulkText} onChange={(e) => setConfirmBulkText(e.target.value)} />
      </Modal>

      {/* Confirm Password Reset */}
      <Modal
        title="Send Password Reset Link"
        open={passwordResetConfirm.open}
        onCancel={() => setPasswordResetConfirm({ open: false, user: null })}
        onOk={confirmSendPasswordReset}
        okText="Send Link"
        cancelText="Cancel"
        okButtonProps={{ loading: sendPasswordResetMutation.isLoading }}
      >
        {passwordResetConfirm.user && (
          <div>
            <Typography.Paragraph>
              Are you sure you want to send a password reset link to:
            </Typography.Paragraph>
            <Typography.Paragraph strong style={{ fontSize: 16, marginBottom: 8 }}>
              {passwordResetConfirm.user.first_name} {passwordResetConfirm.user.last_name}
            </Typography.Paragraph>
            <Typography.Paragraph type="secondary">
              {passwordResetConfirm.user.email}
            </Typography.Paragraph>
            <Typography.Paragraph type="secondary" style={{ marginTop: 16, fontSize: 12 }}>
              The reset link will be valid for 24 hours.
            </Typography.Paragraph>
          </div>
        )}
      </Modal>

      {/* Approve User Request Modal */}
      <Modal
        title="Approve User Request"
        open={approveRequestModal}
        onCancel={() => {
          setApproveRequestModal(false);
          approveForm.resetFields();
          setSelectedRequest(null);
        }}
        onOk={() => approveForm.submit()}
        width={600}
        okText="Approve & Create Account"
        okButtonProps={{ loading: approveRequestMutation.isLoading }}
      >
        {selectedRequest && (
          <div style={{ marginBottom: 24 }}>
            <Alert
              message="User Request Details"
              description={
                <div>
                  <p><strong>Name:</strong> {selectedRequest.first_name} {selectedRequest.last_name}</p>
                  <p><strong>Email:</strong> {selectedRequest.email}</p>
                  <p><strong>Phone:</strong> {selectedRequest.phone || 'Not provided'}</p>
                  {selectedRequest.notes && (
                    <p><strong>Notes:</strong> {selectedRequest.notes}</p>
                  )}
                </div>
              }
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
            <Text type="secondary">
              Fill in the remaining user information below. The user will receive an email to set their password.
            </Text>
          </div>
        )}
        <Form
          form={approveForm}
          layout="vertical"
          onFinish={confirmApproveRequest}
        >
          <Form.Item
            name="role_id"
            label="Role"
            rules={[{ required: true, message: 'Please select a role' }]}
          >
            <Select
              placeholder="Select role"
              loading={loadingRoles}
              showSearch
              optionFilterProp="label"
              options={(rolesData?.roles || []).map((role: Role) => ({ value: role.id, label: role.display_name || role.name }))}
            />
          </Form.Item>

          <Form.Item name="department_id" label="Department">
            <Select placeholder="Select department" allowClear>
              {departments.map((dept: Department) => (
                <Select.Option key={dept.id} value={dept.id}>
                  {dept.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="department_ids" label="Departments (Multiple)">
            <Select mode="multiple" placeholder="Select departments">
              {departments.map((dept: Department) => (
                <Select.Option key={dept.id} value={dept.id}>
                  {dept.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="extension" label="Extension">
            <Input placeholder="Enter extension" />
          </Form.Item>

          <Form.Item name="ringcentral_extension" label="RingCentral Extension">
            <Input placeholder="Enter RingCentral extension" />
          </Form.Item>

          <Form.Item name="is_active" label="Active" valuePropName="checked" initialValue={true}>
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      {/* Reject User Request Modal */}
      <Modal
        title="Reject User Request"
        open={rejectRequestModal}
        onCancel={() => {
          setRejectRequestModal(false);
          setSelectedRequest(null);
        }}
        onOk={() => {
          const reasonInput = document.querySelector('#rejection-reason') as HTMLTextAreaElement;
          confirmRejectRequest(reasonInput?.value || undefined);
        }}
        width={500}
        okText="Reject Request"
        okButtonProps={{ danger: true, loading: rejectRequestMutation.isLoading }}
      >
        {selectedRequest && (
          <div>
            <Alert
              message="Are you sure you want to reject this request?"
              description={
                <div>
                  <p><strong>Name:</strong> {selectedRequest.first_name} {selectedRequest.last_name}</p>
                  <p><strong>Email:</strong> {selectedRequest.email}</p>
                </div>
              }
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
            />
            <div style={{ marginTop: 16 }}>
              <Text strong>Rejection Reason (Optional)</Text>
              <Input.TextArea
                id="rejection-reason"
                rows={3}
                placeholder="Enter reason for rejection (optional)"
                style={{ marginTop: 8 }}
              />
            </div>
          </div>
        )}
      </Modal>
    </Layout>
  );
};

export default UsersAntd;

